import React from "react";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DeviceDetails from "../pages/DeviceDetails";
import { useCbomContext } from "../context/CbomContext";
import {
  fetchDeviceHealth,
  fetchDeviceMeta,
  getDeviceById,
} from "../services/apiServices";
import { formatDate } from "../utils/formatDate";
import { sortData } from "../utils/sortData";
import { useModal } from "../context/ModalContext";
import { Device } from "../schemas/device.schema";
import { useProjectContext } from "context/ProjectContext";

interface DeviceHealth {
  device_id: string;
  last_heart_beat_time: string | Date | null;
  last_cbom_sync_time: string | Date | null;
  no_of_cbom_records: number;
  // no_of_va_records?: number | null;
  cbom_status: "first" | "intermediate" | "incremental" | "complete";
  // va_status?: string | null;
  ip_address?: string;
  status?: "active" | "inactive";
}
const fieldLabels: Record<keyof DeviceHealth, string> = {
  device_id: "Device ID",
  ip_address: "IP Address",
  status: "Status",
  last_heart_beat_time: "Last Heart Beat Time",
  last_cbom_sync_time: "Last CBOM Sync Time",
  no_of_cbom_records: "No of CBOM records",
  // no_of_va_records: "No of VA records",
  cbom_status: "CBOM status",
  // va_status: "VA status",
};
const DeviceTable: React.FC = () => {
  const { state: modalState, dispatch } = useModal();
  const { state } = useProjectContext();
  const {
    setSelectedDeviceId,
    showCbomComponent,
    setShowCbomComponent,
    showAddDevice,
    setShowAddDevice,
  } = useCbomContext();
  const deviceFields = Object.keys(fieldLabels) as (keyof DeviceHealth)[];
  const [deviceData, setDeviceData] = useState<DeviceHealth[]>([]);
  const [currentItems, setCurrentItems] = useState<DeviceHealth[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSources, setFilteredSources] = useState<DeviceHealth[]>([]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(filteredSources.length / itemsPerPage);
  const [sortField, setSortField] = useState<keyof DeviceHealth>("device_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const navigate = useNavigate();
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  useEffect(() => {
    const fetchAndMergeData = async () => {
      try {
        if (state.currentProjectID) {
          const [healthData, metaMap] = await Promise.all([
            fetchDeviceHealth(state.currentProjectID),
            fetchDeviceMeta(state.currentProjectID),
          ]);

          const enrichedData = healthData.map((device) => ({
            ...device,
            ip_address: metaMap[device.device_id]?.ip_address || "N/A",
            status: metaMap[device.device_id]?.status || "inactive",
          }));
          const sortedData = sortData(enrichedData, "device_id", "asc");
          setDeviceData(sortedData);
        }
      } catch (error) {
        console.error("Error fetching device data:", error);
      }
    };
    fetchAndMergeData();
  }, [
    localStorage.getItem("authToken"),
    state.currentProjectID,
    modalState.activeModal,
  ]);

  console.log(state);
  useEffect(() => {
    let filtered = deviceData;
    if (searchTerm) {
      filtered = deviceData.filter(
        (e) =>
          e &&
          Object.values(e)
            .filter(
              (value) => typeof value === "string" || typeof value === "number"
            )
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSources(filtered);
    } else {
      setFilteredSources(deviceData);
    }
  }, [searchTerm, deviceData]);
  useEffect(() => {
    if (sortField) {
      const sortedData = sortData(filteredSources, sortField, sortOrder);
      setFilteredSources(sortedData);
    }
  }, [sortField, sortOrder]);
  useEffect(() => {
    setCurrentItems(
      Array.isArray(filteredSources)
        ? filteredSources.slice(indexOfFirstItem, indexOfLastItem)
        : []
    );
  }, [filteredSources, currentPage, itemsPerPage]);

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  const handleCbomClick = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setShowCbomComponent(true);
  };
  const handleDeviceClick = async (deviceId: string) => {
    try {
      if (state.currentProjectID) {
        const response = await getDeviceById(deviceId, state.currentProjectID); // API call returns device data
        const data = response.data;
        dispatch({ type: "OPEN", modal: "EXISTING_DEVICE", payload: data });
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleSort = (field: keyof DeviceHealth) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <>
      <div className="h-full p-2 bg-white rounded-lg flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-custom-blue7 pl-2">
            Device Health page
          </h1>
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
              className="w-[300px] pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue5 bg-custom-blue8"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
          <button
            className="p-2 bg-custom-blue7 rounded-full"
            onClick={() => dispatch({ type: "OPEN", modal: "ADD_NEW_DEVICE" })}
          >
            <Plus size={20} className="text-gray-50" />
          </button>
        </div>
        <div className="flex flex-col bg-custom-blue8 border rounded-md p-2 flex-grow min-h-0">
          {/* <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold text-custom-blue7 pl-2">
                    Devices
                </h1>
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue5 bg-white"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
          </div> */}
          <div className="overflow-auto flex-grow custom-scrollbar">
            <table
              id="data-table"
              className="min-w-full bg-white shadow border rounded-md"
            >
              <thead className="sticky top-0 z-10 bg-custom-blue0">
                <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                  {deviceFields.map((field) => (
                    <th
                      key={field}
                      className="p-2"
                      onClick={() => handleSort(field)}
                    >
                      {fieldLabels[field]}
                      {sortField === field && (
                        <span className="ml-1">
                          {sortOrder === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems &&
                Array.isArray(currentItems) &&
                currentItems.length > 0 ? (
                  currentItems.map((source, index) => {
                    return (
                      <tr
                        key={source.device_id}
                        className={`${
                          index % 2 === 1 ? "bg-custom-blue0" : ""
                        } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                      >
                        {deviceFields.map((field) => (
                          <td key={field} className="p-2">
                            {field === "device_id" ? (
                              <button
                                onClick={() =>
                                  handleDeviceClick(source.device_id)
                                }
                                className="text-blue-600 underline hover:text-blue-800"
                              >
                                {source[field] ? String(source[field]) : "N/A"}
                              </button>
                            ) : field === "no_of_cbom_records" ? (
                              <button
                                onClick={() =>
                                  handleCbomClick(source.device_id)
                                }
                                className="text-blue-600 underline hover:text-blue-800"
                              >
                                {source[field] ? String(source[field]) : "N/A"}
                              </button>
                            ) : field === "last_heart_beat_time" ||
                              field === "last_cbom_sync_time" ? (
                              formatDate(source[field])
                            ) : source[field] ? (
                              String(source[field])
                            ) : (
                              "N/A"
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={15}
                      className="text-custom-blue7 p-2 text-center"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center space-x-2">
              <select
                className="border rounded-md p-2 bg-white"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                {/* <option value={5}>5</option> */}
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
              </select>
              <span className="text-custom-blue7">Results per page</span>
            </div>
            <div className="flex items-center space-x-2 text-custom-blue7">
              <span>
                Displaying {indexOfFirstItem + 1}-
                {Math.min(indexOfLastItem, filteredSources.length)} of{" "}
                {filteredSources.length}
              </span>
              <button
                className="p-2 bg-white rounded-md"
                onClick={prevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                className="p-2 bg-white rounded-md"
                onClick={nextPage}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
        {showCbomComponent && <DeviceDetails />}
      </div>
      {/* </div> */}
    </>
  );
};

export default DeviceTable;
